export interface FormData {
  address: string;
  city: string;
  zipCode: string;
  dwell: string;
  dwellCategory: string;
  chargers: string;
  evs: string;
  type: string;
  hardware: string;
  install: string;
  pricingType: string;
  pricePerKwh?: string;
  pricePerMin?: string;
  pricePerHour?: string;
  flatSessionFee?: string;
  idleFee?: string;
  gracePeriod?: string;
  maintenance: string;
  networkPerPort: string;
  grant: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  clientId?: string;
  ownerStatus?: string;
  householdIncome?: string;
  householdSize?: string;
  excludeIncentives?: boolean;

  // Tax benefits fields
  applyTaxBenefits?: boolean;
  businessTaxRate?: string;
  section179Limit?: string;
  bonusDepreciationPct?: string;

  // Add profit sharing properties
  customerSplit?: string;
  softwareProviderSplit?: string;
  operatorSplit?: string;
  customerProfit?: number;
  softwareProviderProfit?: number;
  operatorProfit?: number;

  disableUtilityEnergyInfo?: boolean;
}

export interface ReportData {
  chargingRev5: number;
  addedDwell: string;
  addSpend: string;
  traffic: string;
  indirectRev5: number;
  totalRev5: number;
  install: number;
  hardware: number;
  network: number;
  maintenance: number;
  utilityCost5: number;
  grant: number;
  netProfit5: number;
  kWhYearArr: number[];
  chargingRevArr: number[];
  indirectArr: number[];
  totalRevArr: number[];
  co2Arr: number[];
  gasArr: number[];

  // Tax benefits fields
  applyTaxBenefits?: boolean;
  businessTaxRate?: number;
  section179Limit?: number;
  bonusDepreciationPct?: number;
  qualifyingSpend?: number;
  taxSavings?: number;
  upfrontAfterTax?: number;

  // Add profit sharing properties
  customerSplit?: string;
  softwareProviderSplit?: string;
  operatorSplit?: string;
  customerProfit?: number;
  softwareProviderProfit?: number;
  operatorProfit?: number;
}

export const typePresets: Record<
  string,
  { whpm: number; hardware: number; install: number }
> = {
  "6.7-7.7-650-1500": { whpm: 7.2, hardware: 650, install: 1500 },
  "8.3-9.6-800-1600": { whpm: 9.6, hardware: 800, install: 1600 },
  "6.7-7.7-700-1500": { whpm: 7.2, hardware: 700, install: 1500 },
  "6.7-7.7-600-1400": { whpm: 7.2, hardware: 600, install: 1400 },
  "8.3-9.6-550-1400": { whpm: 9.6, hardware: 550, install: 1400 },
  "10.0-11.5-900-1700": { whpm: 11.5, hardware: 900, install: 1700 },
  "16.6-19.2-1200-2000": { whpm: 19.2, hardware: 1200, install: 2000 },
};

export const evDriversByCategory: Record<
  string,
  { low: number; medium: number; high: number }
> = {
  "Shopping Centers / Retail": {
    low: 2100,
    medium: 5000,
    high: 9500,
  },
  "Apartment Complex / Multi-family": {
    low: 2500,
    medium: 6500,
    high: 12000,
  },
  "Hotels / Lodging": {
    low: 3500,
    medium: 7500,
    high: 13000,
  },
  "Office Buildings": {
    low: 3000,
    medium: 6000,
    high: 10000,
  },
  "Restaurants / Cafés": {
    low: 1500,
    medium: 4000,
    high: 8000,
  },
  "Theaters / Entertainment": {
    low: 2500,
    medium: 6500,
    high: 12000,
  },
  "Gyms / Fitness": {
    low: 1800,
    medium: 4500,
    high: 8500,
  },
  "Hospitals / Medical": {
    low: 2000,
    medium: 5000,
    high: 9000,
  },
  "Universities / Campus": {
    low: 2500,
    medium: 6000,
    high: 10000,
  },
  "Parking Garages / Lots": {
    low: 2000,
    medium: 5000,
    high: 10000,
  },
  "Household / Home": {
    low: 1000,
    medium: 2363,
    high: 4300,
  },
  "Grocery Stores / Supermarkets": {
    low: 2000,
    medium: 5500,
    high: 11500,
  },
};

const metrics: Record<
  string,
  { rev: number; dwell: number; traffic: number; spend: number }
> = {
  "Shopping Centers / Retail": {
    rev: 12264,
    dwell: 90,
    traffic: 0.002,
    spend: 50,
  },
  "Apartment Complex / Multi-family": {
    rev: 0,
    dwell: 0,
    traffic: 0,
    spend: 0,
  },
  "Hotels / Lodging": { rev: 5000, dwell: 0, traffic: 0.0014, spend: 20 },
  "Office Buildings": { rev: 0, dwell: 480, traffic: 0, spend: 0 },
  "Restaurants / Cafés": { rev: 778, dwell: 90, traffic: 0.0014, spend: 20 },
  "Theaters / Entertainment": {
    rev: 4360,
    dwell: 90,
    traffic: 0.0014,
    spend: 20,
  },
  "Gyms / Fitness": { rev: 1355, dwell: 30, traffic: 0.0016, spend: 20 },
  "Hospitals / Medical": { rev: 0, dwell: 0, traffic: 0, spend: 0 },
  "Universities / Campus": { rev: 0, dwell: 0, traffic: 0, spend: 0 },
  "Parking Garages / Lots": {
    rev: 2272,
    dwell: 120,
    traffic: 0.0014,
    spend: 4,
  },
  "Household / Home": {
    rev: 0,
    dwell: 720,
    traffic: 0.0,
    spend: 0,
  },
  "Grocery Stores / Supermarkets": {
    rev: 0,
    dwell: 45,
    traffic: 0.04,
    spend: 0,
  },
};

export const calculateReport = (
  formData: FormData,
  utilPct: number = 0.15,
  utilRate: number = 0.18,
  customPresets?: Record<
    string,
    { whpm: number; hardware: number; install: number }
  >
): ReportData => {
  const dwell = parseFloat(formData.dwell) || 1;
  const chargers = parseFloat(formData.chargers) || 1;
  const evLevel = formData.evs.toLowerCase() as "low" | "medium" | "high";
  const category = formData.dwellCategory || "Shopping Centers / Retail";

  // Get EV driver count from evDriversByCategory
  const evs = evDriversByCategory[category]?.[evLevel] || 5000;

  // Combine custom presets with default typePresets
  const allPresets = customPresets
    ? { ...typePresets, ...customPresets }
    : typePresets;

  // Get the preset with fallback to default values if not found
  const preset = allPresets[formData.type] || {
    whpm: 7.2,
    hardware: 0,
    install: 0,
  };
  const { whpm } = preset;

  const hardware = parseFloat(formData.hardware) || 0;
  const install = parseFloat(formData.install) || 0;
  const grant = parseFloat(formData.grant) || 0;
  let maintenance = 0;
  for (let y = 0; y < 5; y++) {
    maintenance +=
      (parseFloat(formData.maintenance) || 0) * chargers * Math.pow(1.05, y);
  }

  const networkPerPort = parseFloat(formData.networkPerPort) || 0;
  const networkTotal = networkPerPort * chargers;

  // TAX BENEFITS CALCULATION
  const applyTaxBenefits = formData.applyTaxBenefits || false;
  const businessTaxRate = parseFloat(formData.businessTaxRate || "21") / 100;
  const section179Limit = parseFloat(formData.section179Limit || "2500000");
  const bonusDepreciationPct =
    Math.min(
      100,
      Math.max(0, parseFloat(formData.bonusDepreciationPct || "0"))
    ) / 100;

  const qualifyingSpend = hardware + install;

  let taxSavings = 0;
  if (applyTaxBenefits && qualifyingSpend > 0) {
    const base179 = Math.min(qualifyingSpend, section179Limit);
    const bonusBase = Math.max(0, qualifyingSpend - base179);
    const totalDeduct = base179 + bonusBase * bonusDepreciationPct;
    taxSavings = totalDeduct * businessTaxRate;
  }

  const totalUpfrontCost = install + hardware + networkTotal - grant;
  const upfrontAfterTax = Math.max(0, totalUpfrontCost - taxSavings);

  // Pricing model
  let sellRate = 0;
  let connFee = 0;
  const pricingType = formData.pricingType;
  if (pricingType === "kwh" || pricingType === "kwh_idle") {
    sellRate = parseFloat(formData.pricePerKwh || "0");
  } else if (pricingType === "minute" || pricingType === "minute_idle") {
    connFee = parseFloat(formData.pricePerMin || "0") / 100;
  } else if (pricingType === "hour") {
    connFee = parseFloat(formData.pricePerHour || "0");
  } else if (pricingType === "session") {
    connFee = parseFloat(formData.flatSessionFee || "0");
  }

  // EV growth & revenues
  let kWhYearArr: number[] = [];
  let chargingRevArr: number[] = [];
  let indirectArr: number[] = [];
  let totalRevArr: number[] = [];
  let yearEVs = evs; // Use the EV count from evDriversByCategory
  let kWh5 = 0;
  let chargingRev5 = 0;
  let indirect5 = 0;

  // --- Emissions & efficiency constants ---
  const KG_CO2_PER_GAL_GAS = 8.887; // EPA tailpipe factor (kg CO2 per gallon gasoline)
  const EV_MI_PER_KWH = 3.5; // EV efficiency (miles/kWh) — adjust if you prefer
  const BASELINE_ICE_MPG = 28; // Baseline ICE fuel economy (miles/gal) — adjust if you prefer

  const cat = formData.dwellCategory || "";
  let m = metrics[cat] || { rev: 0, dwell: 0, traffic: 0, spend: 0 };
  let indirectBase = m.rev * utilPct * chargers;

  // Optional runtime adjustment for Grocery Stores / Supermarkets spend
  if (cat === "Grocery Stores / Supermarkets") {
    const avgBasket = (window as any).siteAvgBasket || null;
    if (avgBasket) {
      m = { ...m, spend: 0.05 * avgBasket };
    }
  }

  for (let y = 0; y < 5; y++) {
    if (y > 0) yearEVs *= 1.2;
    const windowMin = dwell > 720 ? 1440 : 720;
    const perCh = Math.floor(windowMin / dwell);
    const maxSess = perCh * chargers;
    const sessDay = Math.min(yearEVs, maxSess) * utilPct;
    const effDwell = Math.min(dwell, windowMin);

    const kWhPerSession = (effDwell / 60) * whpm;
    const kWhYear = sessDay * kWhPerSession * 365;
    kWhYearArr.push(kWhYear);

    let chargingRev;
    if (pricingType === "kwh" || pricingType === "kwh_idle") {
      chargingRev = kWhYear * sellRate;
    } else if (pricingType === "minute" || pricingType === "minute_idle") {
      chargingRev = sessDay * 365 * effDwell * connFee;
    } else if (pricingType === "hour") {
      chargingRev = sessDay * 365 * (effDwell / 60) * connFee;
    } else if (pricingType === "session") {
      chargingRev = sessDay * 365 * connFee;
    } else {
      chargingRev = 0;
    }
    chargingRevArr.push(chargingRev);

    let indirectVal;
    if (y === 0) indirectVal = indirectBase * 0.2;
    else if (y === 1) indirectVal = indirectBase * 0.6;
    else if (y === 2) indirectVal = indirectBase * 1.0;
    else if (y === 3) indirectVal = indirectBase * 1.1;
    else indirectVal = indirectBase * 1.15;

    indirectArr.push(indirectVal);

    totalRevArr.push(chargingRev + indirectVal);
    kWh5 += kWhYear;
    chargingRev5 += chargingRev;
    indirect5 += indirectVal;
  }

  const totalRev5 = chargingRev5 + indirect5;
  const utilityCost5 = kWh5 * utilRate;

  // Optional: represent full utility bill including service & demand
  const utilityBill5 = utilityCost5 + 6000 * 5 + 82.8 * 5;

  const netProfit5 =
    totalRev5 -
    install -
    hardware -
    networkTotal -
    maintenance -
    utilityCost5 +
    grant;

  // CO2 impact (derive gasoline displaced from EV kWh via miles)
  // miles = kWh * EV efficiency; gallons avoided = miles / ICE mpg; CO2 = gallons * 8.887 kg
  const milesArr = kWhYearArr.map((kwh) => kwh * EV_MI_PER_KWH);
  const gallonsArr = milesArr.map((miles) => miles / BASELINE_ICE_MPG);
  const co2Arr = gallonsArr.map((gal) => gal * KG_CO2_PER_GAL_GAS);

  // Update cash flow to use after-tax upfront cost
  const cashFlowData = [-upfrontAfterTax];
  for (let i = 0; i < 5; i++) {
    const yVal =
      chargingRevArr[i] +
      indirectArr[i] -
      maintenance / 5 -
      kWhYearArr[i] * utilRate;
    cashFlowData.push(cashFlowData[i] + yVal);
  }

  return {
    chargingRev5: Math.round(chargingRev5),
    addedDwell: m.dwell ? `${m.dwell} min` : "-",
    addSpend: m.spend ? `$${m.spend} per customer` : "-",
    traffic: m.traffic ? `${(m.traffic * 100).toFixed(2)}%` : "-",
    indirectRev5: Math.round(indirect5),
    totalRev5: Math.round(totalRev5),
    install,
    hardware,
    network: networkTotal,
    maintenance: Number(maintenance.toFixed(2)),
    utilityCost5: Number(utilityCost5.toFixed(2)),
    grant,
    netProfit5: Math.round(netProfit5),
    kWhYearArr,
    chargingRevArr,
    indirectArr,
    totalRevArr,
    co2Arr,
    gasArr: gallonsArr,

    // Tax benefits results
    applyTaxBenefits,
    businessTaxRate: businessTaxRate * 100,
    section179Limit,
    bonusDepreciationPct: bonusDepreciationPct * 100 || 0,
    qualifyingSpend,
    taxSavings,
    upfrontAfterTax,
  };
};
